import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Ticket, Plus, Trash2, Copy, Percent, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', discount_type: 'percentage', discount_value: '', max_discount: '', max_uses: '', valid_from: '', valid_until: '', min_booking_amount: '', active: true });

  const loadData = useCallback(async () => {
    try { const res = await api.get('/coupons'); setCoupons(res.data); }
    catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, discount_value: parseFloat(form.discount_value) || 0 };
      if (form.max_discount) payload.max_discount = parseFloat(form.max_discount);
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses);
      if (form.min_booking_amount) payload.min_booking_amount = parseFloat(form.min_booking_amount);
      if (!form.valid_from) payload.valid_from = null;
      if (!form.valid_until) payload.valid_until = null;
      await api.post('/coupons', payload);
      toast.success('Coupon created!');
      setModalOpen(false);
      setForm({ code: '', name: '', discount_type: 'percentage', discount_value: '', max_discount: '', max_uses: '', valid_from: '', valid_until: '', min_booking_amount: '', active: true });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try { await api.delete(`/coupons/${id}`); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(n || 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>;

  return (
    <div data-testid="coupons-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope']">Coupons & Discounts</h1>
          <p className="text-stone-500 mt-1">Create and manage promotional discount codes</p>
        </div>
        <Button onClick={() => setModalOpen(true)} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="add-coupon-btn">
          <Plus className="w-4 h-4 mr-2" />Create Coupon
        </Button>
      </div>

      {coupons.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
          <Ticket className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500 mb-4">No coupons yet</p>
          <Button onClick={() => setModalOpen(true)} variant="outline">Create your first coupon</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coupons.map(c => (
            <div key={c.id} className={`bg-white border rounded-md p-4 relative ${c.active ? 'border-stone-200' : 'border-stone-100 opacity-60'}`} data-testid={`coupon-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sage-700 text-lg tracking-wider">{c.code}</span>
                    <button onClick={() => copyCode(c.code)} className="text-stone-300 hover:text-stone-500"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="text-sm text-stone-600">{c.name}</p>
                </div>
                <button onClick={() => handleDelete(c.id)} className="text-stone-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center gap-2 mb-2">
                {c.discount_type === 'percentage' ? (
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                    <Percent className="w-3.5 h-3.5" />{c.discount_value}% off
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                    <DollarSign className="w-3.5 h-3.5" />{fmt(c.discount_value)} off
                  </span>
                )}
                {c.max_discount && <span className="text-xs text-stone-400">max {fmt(c.max_discount)}</span>}
                {!c.active && <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">Inactive</span>}
              </div>
              <div className="text-xs text-stone-400 space-y-0.5">
                {c.valid_from && <p>Valid: {c.valid_from} → {c.valid_until || 'No end'}</p>}
                {c.max_uses && <p>Uses: {c.times_used || 0}/{c.max_uses}</p>}
                {c.min_booking_amount && <p>Min. booking: {fmt(c.min_booking_amount)}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-['Manrope']">Create Coupon</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="WELCOME20" required className="border-stone-300 font-mono" data-testid="coupon-code-input" /></div>
              <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Welcome Discount" required className="border-stone-300" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}>
                  <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed">Fixed Amount</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>{form.discount_type === 'percentage' ? 'Discount %' : 'Amount (₹)'}</Label><Input type="number" min="0" step="0.01" value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} required className="border-stone-300" data-testid="discount-value-input" /></div>
              <div className="space-y-1"><Label>Max Discount (₹)</Label><Input type="number" min="0" value={form.max_discount} onChange={e => setForm({...form, max_discount: e.target.value})} placeholder="Optional" className="border-stone-300" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Valid From</Label><Input type="date" value={form.valid_from} onChange={e => setForm({...form, valid_from: e.target.value})} className="border-stone-300" /></div>
              <div className="space-y-1"><Label>Valid Until</Label><Input type="date" value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})} className="border-stone-300" /></div>
              <div className="space-y-1"><Label>Max Uses</Label><Input type="number" min="1" value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})} placeholder="Unlimited" className="border-stone-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Min Booking (₹)</Label><Input type="number" min="0" value={form.min_booking_amount} onChange={e => setForm({...form, min_booking_amount: e.target.value})} placeholder="No minimum" className="border-stone-300" /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} className="data-[state=checked]:bg-emerald-700" /><Label>Active</Label></div>
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white">Create Coupon</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
